import { createFileRoute } from '@tanstack/react-router';
import { ArrowRight, Calendar, Clock, Sparkles } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Footer from '@/components/Footer';
import Navbar from '@/components/Navbar';

export const Route = createFileRoute('/operations')({
  component: OperationsPage,
});

const CADENCE = [
  {
    icon: <Clock size={20} className="text-purple-400" />,
    period: 'Daily',
    tasks: [
      'Design pipeline runs',
      'New listings published',
      'CX inbox triage',
      'Fulfillment exceptions',
    ],
  },
  {
    icon: <Calendar size={20} className="text-purple-400" />,
    period: 'Weekly',
    tasks: [
      'Pricing + inventory sync',
      'Ad-spend review',
      'Reviews operations',
      'Cross-marketplace alignment',
    ],
  },
  {
    icon: <Calendar size={20} className="text-purple-400" />,
    period: 'Monthly',
    tasks: [
      'SKU-level P&L',
      'Forecast vs. actual',
      'Stack performance review',
      'Hiring & capacity plan',
    ],
  },
  {
    icon: <Sparkles size={20} className="text-purple-400" />,
    period: 'Quarterly',
    tasks: [
      'Brand strategy review',
      'New category bets',
      'Tooling roadmap',
      'Investor / board update',
    ],
  },
];

const STACK_PILLARS = [
  {
    title: 'Storefront',
    aiAssist: 'Templated themes, automated content updates',
    human: 'Custom in-house code (Elzary) or Shopify setup, design choices, brand IA',
  },
  {
    title: 'Design pipeline',
    aiAssist: 'Text-to-image, brand-consistent variants',
    human: 'Art direction, brand guardrails, final selection',
  },
  {
    title: 'Listing engine',
    aiAssist: 'SEO copy, tags, keyword research, A/B variations',
    human: 'Brand voice review, category strategy (Etsy / Amazon / eBay)',
  },
  {
    title: 'Fulfillment routing',
    aiAssist: 'Partner routing by SKU + region, exception detection',
    human: 'Vendor relationships, SLA negotiation, escalations',
  },
  {
    title: 'CX agent',
    aiAssist: 'LLM-drafted replies, sentiment tagging',
    human: 'Brand-voice review, tricky cases, escalations',
  },
  {
    title: 'Growth',
    aiAssist: 'Trend detection, creative variations, audience targeting',
    human: 'Channel mix, budget allocation, narrative',
  },
];

function OperationsPage() {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>How we operate a POD brand — AITOS Labs LLC</title>
        <meta
          name="description"
          content="The AITOS ops stack and operating cadence. Opinionated, AI-leveraged, built by operators who run the brands themselves."
        />
        <link rel="canonical" href="https://aitos.app/operations" />
      </Helmet>

      <Navbar />

      <section className="pt-32 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          <p className="text-purple-400 text-xs tracking-widest mb-4">THE PLAYBOOK</p>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            How we operate a POD brand.
          </h1>
          <p className="text-gray-300 text-lg max-w-3xl">
            The AITOS ops stack and operating cadence — opinionated, AI-leveraged, built by
            operators who run the brands themselves.
          </p>
        </div>
      </section>

      {/* Cadence */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-8">Operating cadence</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {CADENCE.map((c) => (
              <div
                key={c.period}
                className="bg-gray-900 rounded-2xl p-6 border border-gray-800 hover:border-purple-500/30 transition-colors"
              >
                <div className="flex items-center gap-2 mb-4">
                  {c.icon}
                  <h3 className="text-xl font-bold">{c.period}</h3>
                </div>
                <ul className="space-y-2">
                  {c.tasks.map((t) => (
                    <li key={t} className="text-sm text-gray-300 flex items-start gap-2">
                      <span className="text-purple-400 mt-1.5 size-1 rounded-full bg-purple-400 flex-shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stack deep dive */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-2">Stack deep-dive</h2>
          <p className="text-gray-400 mb-8">What's AI-assisted vs. human-in-the-loop, by pillar.</p>
          <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-3 bg-gray-800/50 text-xs font-semibold text-gray-400 uppercase tracking-widest">
              <div className="p-4">Pillar</div>
              <div className="p-4 border-t md:border-t-0 md:border-l border-gray-800">
                AI-assisted
              </div>
              <div className="p-4 border-t md:border-t-0 md:border-l border-gray-800">
                Human-in-the-loop
              </div>
            </div>
            {STACK_PILLARS.map((p) => (
              <div
                key={p.title}
                className="grid grid-cols-1 md:grid-cols-3 border-t border-gray-800"
              >
                <div className="p-4 font-medium text-white">{p.title}</div>
                <div className="p-4 text-sm text-gray-300 md:border-l border-gray-800">
                  {p.aiAssist}
                </div>
                <div className="p-4 text-sm text-gray-300 md:border-l border-gray-800">
                  {p.human}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dual CTA */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
          <a
            href="/#cta?intent=audit"
            className="group bg-gradient-to-br from-purple-900/30 to-pink-900/20 rounded-2xl p-8 border border-purple-500/30 hover:border-purple-500/60 transition-all"
          >
            <p className="text-purple-300 text-xs tracking-widest uppercase mb-2">For sellers</p>
            <h3 className="text-2xl font-bold mb-2">Book a brand audit</h3>
            <p className="text-gray-300 mb-4">
              2 weeks. Diagnostic + action plan. Pre-requisite for any managed engagement.
            </p>
            <span className="inline-flex items-center gap-2 text-white font-medium group-hover:gap-3 transition-all">
              Get started
              <ArrowRight size={16} />
            </span>
          </a>
          <a
            href="/#cta?intent=investor"
            className="group bg-gray-900 rounded-2xl p-8 border border-gray-800 hover:border-purple-500/30 transition-all"
          >
            <p className="text-pink-300 text-xs tracking-widest uppercase mb-2">
              For investors / partners
            </p>
            <h3 className="text-2xl font-bold mb-2">Talk to the founders</h3>
            <p className="text-gray-300 mb-4">
              Operating model, unit economics, and roadmap on request.
            </p>
            <span className="inline-flex items-center gap-2 text-white font-medium group-hover:gap-3 transition-all">
              Get in touch
              <ArrowRight size={16} />
            </span>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
